'use client'

import { useIsMobile } from '@/hooks/use-mobile'
import { SAGE_TEXT } from '@/lib/ui-classes'
import { cn } from '@/lib/utils'
import { CircleNotch } from '@phosphor-icons/react'
import { motion, useReducedMotion } from 'motion/react'
import { useTheme } from 'next-themes'
import { Toaster as Sonner, type ToasterProps } from 'sonner'

/** Every toast leads with a 32px slot, so text lines up with or without a photo. */
const SLOT = 'flex size-8 shrink-0 items-center justify-center'

/** A Sage check that draws itself in: the quiet "done". */
function DrawnCheck() {
  const reduceMotion = useReducedMotion()
  return (
    <span className={SLOT}>
      <svg viewBox="0 0 20 20" className="size-5 text-primary" fill="none" aria-hidden>
        <motion.path
          d="M4.5 10.5 8.2 14 15.5 6"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: reduceMotion ? 1 : 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.35, ease: 'easeOut', delay: 0.1 }}
        />
      </svg>
    </span>
  )
}

function Dot({ className }: { className: string }) {
  return (
    <span className={SLOT}>
      <span className={cn('size-2.5 rounded-full', className)} />
    </span>
  )
}

type Tone = 'done' | 'problem'

/**
 * A toast about one thing (a task, part, or item) leads with its photo, with a
 * status dot: Sage when it went through, Terracotta when it didn't. Without a
 * photo it falls back to the toast type's default mark.
 *
 * toast.success('Descale espresso machine', { description: 'Done', icon: toastPhoto(item.imageUrl) })
 */
export function toastPhoto(src: string | null | undefined, tone: Tone = 'done') {
  if (!src) return undefined
  return (
    <span className="relative block size-8 shrink-0">
      {/* Remote photos from several hosts, already sized small; a plain img keeps toasts light. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt="" className="size-8 rounded-full bg-muted object-cover" />
      <span
        className={cn(
          'absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full ring-2 ring-card',
          tone === 'done' ? 'bg-primary' : 'bg-destructive'
        )}
      />
    </span>
  )
}

/**
 * Toasts are small white pills, the way native apps confirm things: a leading
 * mark or photo, one line naming the thing, one line saying what happened, and
 * a text action. At the top on phones (clear of the search dock, sheets, and
 * nav; swipe up to dismiss), bottom-right on desktop. See DESIGN.md, "Feedback
 * Pattern".
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme()
  const isMobile = useIsMobile()

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      position={isMobile ? 'top-center' : 'bottom-right'}
      mobileOffset={{ top: 'calc(env(safe-area-inset-top) + 8px)', left: 12, right: 12 }}
      offset={24}
      gap={8}
      visibleToasts={3}
      closeButton={!isMobile}
      className="toaster group"
      icons={{
        success: <DrawnCheck />,
        error: <Dot className="bg-destructive" />,
        warning: <Dot className="bg-warning" />,
        info: <Dot className="bg-info" />,
        loading: (
          <span className={SLOT}>
            <CircleNotch className="size-4 animate-spin text-muted-foreground" weight="bold" />
          </span>
        ),
      }}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: cn(
            'group/toast relative flex w-full items-center gap-3 rounded-full border border-border bg-card py-2 pl-2 pr-3',
            'text-card-foreground shadow-lg shadow-black/[0.06] dark:shadow-black/40 md:w-(--width)',
            // Collapsed stack: toasts behind the front one show only their edge.
            '[&>*]:transition-opacity [&[data-expanded=false][data-front=false]>*]:opacity-0'
          ),
          icon: 'flex shrink-0 items-center justify-center',
          // Sonner centers its loader absolutely in the toast; keep it in the slot.
          loader: '!static !transform-none',
          content: 'flex min-w-0 flex-1 flex-col',
          // Names are short; error messages can run long, so both may wrap.
          title: 'line-clamp-2 text-sm font-semibold leading-5 text-foreground',
          description: 'line-clamp-2 text-sm leading-5 text-muted-foreground',
          actionButton: cn(
            '-my-1 h-11 shrink-0 cursor-pointer rounded-full px-3 text-sm font-semibold',
            SAGE_TEXT,
            'transition-colors duration-150 hover:bg-primary/10 active:scale-[0.97] md:h-9',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          ),
          cancelButton:
            '-my-1 h-11 shrink-0 cursor-pointer rounded-full px-3 text-sm font-medium text-muted-foreground hover:text-foreground md:h-9',
          closeButton: cn(
            'absolute -left-1 -top-1 flex size-6 items-center justify-center rounded-full border border-border bg-card',
            'text-muted-foreground opacity-0 shadow-sm transition-opacity group-hover/toast:opacity-100 hover:text-foreground',
            'focus-visible:opacity-100 [&_svg]:size-3'
          ),
        },
      }}
      style={
        {
          // A modal drawer or dialog turns off pointer events on the page;
          // toasts must stay tappable above it.
          pointerEvents: 'auto',
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
